import { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { editPurchasingVendorItemPack } from '@/services/purchasing'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { buildPurchasingVendorItemPackRequest } from './purchasingPackRequest'
import PurchasingPackSelect from './PurchasingPackSelect'

export type PurchasingVendorItemPackDialogProps = {
  open: boolean
  onClose: () => void
  vendorItemIds: number[]
  initialPackText?: string | null
  title?: string
  onSaved: (payload: {
    vendorItemIds: number[]
    packText: string
    recomputedLineCount: number
  }) => void
}

export default function PurchasingVendorItemPackDialog({
  open,
  onClose,
  vendorItemIds,
  initialPackText,
  title = 'Define pack',
  onSaved,
}: PurchasingVendorItemPackDialogProps) {
  const [packText, setPackText] = useState(initialPackText ?? '')
  const [packCount, setPackCount] = useState('')
  const [unitSizeQty, setUnitSizeQty] = useState('')
  const [unitSizeUom, setUnitSizeUom] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setPackText(initialPackText ?? '')
    setPackCount('')
    setUnitSizeQty('')
    setUnitSizeUom('')
  }, [open, initialPackText])

  if (!open) return null

  async function handleSave() {
    const request = buildPurchasingVendorItemPackRequest({
      packText,
      packCount,
      unitSizeQty,
      unitSizeUom,
    })
    if (!request) {
      toast.error('Enter pack text or all structured pack fields')
      return
    }
    const savedPackText =
      request.pack_text ??
      `${request.pack_count}/${request.unit_size_qty} ${request.unit_size_uom ?? ''}`.trim()

    setSaving(true)
    try {
      let recomputedLineCount = 0
      for (const vendorItemId of vendorItemIds) {
        const result = await editPurchasingVendorItemPack(vendorItemId, request)
        recomputedLineCount += result.recomputed_line_count
      }
      onSaved({ vendorItemIds, packText: savedPackText, recomputedLineCount })
      onClose()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save pack'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <Button variant="ghost" iconOnly aria-label="Close" onClick={onClose}>
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          Pack text wins over structured fields. Applies to {vendorItemIds.length} vendor item
          {vendorItemIds.length !== 1 ? 's' : ''}.
        </p>
        <label className="block text-sm" htmlFor="vendor-item-pack-text">
          <span className="font-medium text-gray-700">Pack text</span>
          <div className="mt-1">
            <PurchasingPackSelect
              id="vendor-item-pack-text"
              value={packText}
              onChange={setPackText}
            />
          </div>
        </label>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Count</span>
            <input className="input mt-1 w-full" value={packCount} onChange={(e) => setPackCount(e.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Unit size</span>
            <input className="input mt-1 w-full" value={unitSizeQty} onChange={(e) => setUnitSizeQty(e.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">UOM</span>
            <input className="input mt-1 w-full" value={unitSizeUom} onChange={(e) => setUnitSizeUom(e.target.value)} />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving} onClick={() => void handleSave()}>
            {saving ? 'Saving…' : 'Save pack & recompute'}
          </Button>
        </div>
      </div>
    </div>
  )
}
